/**
 * Window-only chrome for the preferences window: the sidebar item states and
 * the section anchors the window wraps around each section. Section content
 * parts (cards, rows, switches, sheets) live in `@hibro/ui-client`
 * (`settings-page.tsx`), shared with module-contributed sections.
 */
import { cn } from '@hibro/ui-client';
import type { ReactNode } from 'react';

/**
 * Unselected sidebar item on the module container.
 * Hover is 6% / 10% white. Press is 10% / 20% white.
 */
export const SIDEBAR_PRESS =
  'cursor-pointer hover:bg-(--app-base-black-a6) focus-visible:bg-(--app-base-black-a10) focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--app-base-brand) active:bg-(--app-base-black-a10) dark:hover:bg-(--app-base-white-a10) dark:focus-visible:bg-(--app-base-white-a20) dark:active:bg-(--app-base-white-a20)';

/**
 * Selected sidebar item, same accent treatment as the workbench rail:
 * `--app-color-accent-subtle` fill, press steps to brand 20%.
 */
export const SIDEBAR_SELECTED =
  'cursor-pointer bg-(--app-color-accent-subtle) focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-(--app-base-brand) active:bg-(--app-base-brand-a20)';

/** Content-box height of the pane scroller, published by the window shell. */
export const ANCHOR_LAST_MIN_HEIGHT_VAR = '--settings-anchor-last-min-h';

/**
 * One section of a page, marked for sidebar anchors and the scroll spy. The
 * last section is at least one pane tall, so its anchor can still bring it to
 * the top.
 */
export function AnchorSection({
  id,
  last,
  hidden,
  className,
  children,
}: {
  id: string;
  last?: boolean;
  hidden?: boolean;
  className?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div
      data-settings-anchor={id}
      hidden={hidden}
      className={cn(last && 'min-h-(--settings-anchor-last-min-h)', className)}
    >
      {children}
    </div>
  );
}
